import { toastNotifications } from '@uag/react-core';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { useSolveCornerPlacement } from 'api/queries/forming/forming';
import { buildSolveCornerPlacementRequest } from 'app/modules/wallforming/components/setting-container/corner-placement/utils/buildSolveCornerPlacementRequest';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

import type { SolveCornerPlacementResponse } from 'api/models';

interface CornerPlacementCacheEntry {
    response: SolveCornerPlacementResponse;
    errorShown: boolean;
}

interface AutomatedCornerPlacementProps {
    setLoadingState?: (isLoading: boolean) => void;
}

export const useGetAutomatedCornerPlacement = ({ setLoadingState }: AutomatedCornerPlacementProps) => {
    const { t } = useTranslation();
    const solveCornerPlacementMutation = useSolveCornerPlacement();
    const isPendingRef = useRef<boolean>(false);
    const responseCacheRef = useRef<Map<string, CornerPlacementCacheEntry>>(new Map());

    const { selectedPoiIndex, existingPlacementIds } = usePlacementStore(
        useShallow((state) => ({
            selectedPoiIndex: state.poiSelection.current.index,
            existingPlacementIds:
                state.poiSelection.current.index === undefined
                    ? ''
                    : Object.values(state.pois[state.poiSelection.current.index]?.articles ?? {})
                          .flat()
                          .sort()
                          .join(',')
        }))
    );

    const [searchParams] = useSearchParams();
    const sessionguid = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);

    return useCallback(async () => {
        if (selectedPoiIndex === undefined || isPendingRef.current) {
            return;
        }

        isPendingRef.current = true;
        setLoadingState?.(true);

        try {
            const state = usePlacementStore.getState();
            const solverWeights = state.automatedPlacementSettingsByType.Corners.solverCostWeights;
            const cacheKey = `${selectedPoiIndex}:${existingPlacementIds}:${JSON.stringify(solverWeights)}`;
            const cachedEntry = responseCacheRef.current.get(cacheKey);
            if (cachedEntry) {
                if (cachedEntry.response.accepted) {
                    state.setPlacementSolutions(cachedEntry.response.solutions ?? []);
                } else if (!cachedEntry.errorShown) {
                    toastNotifications.error({
                        title: t('error'),
                        message: `${cachedEntry.response.errorCode}: ${cachedEntry.response.message}`
                    });
                    cachedEntry.errorShown = true;
                }
                return;
            }

            const payload = buildSolveCornerPlacementRequest({
                state,
                sessionId: sessionguid,
                systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                poiIndex: selectedPoiIndex,
                mode: 'Automatic'
            });

            const response = await solveCornerPlacementMutation.mutateAsync({
                data: payload
            });
            responseCacheRef.current.set(cacheKey, { response, errorShown: false });

            if (!response.accepted) {
                toastNotifications.error({
                    title: t('error'),
                    message: `${response.errorCode}: ${response.message}`
                });
                return;
            }

            state.setPlacementSolutions(response.solutions ?? []);
            toastNotifications.success({ title: t('success'), message: response.message });
        } catch {
            toastNotifications.error({ title: t('error'), message: t('errorAutoCornerPlacement') });
        } finally {
            isPendingRef.current = false;
            setLoadingState?.(false);
        }
    }, [existingPlacementIds, selectedPoiIndex, sessionguid, setLoadingState, solveCornerPlacementMutation, t]);
};
